/*
 * Copyright (C) 2005 - 2023. Cloud Software Group, Inc. All Rights Reserved. Confidential & Proprietary.
 * Licensed pursuant to commercial Cloud Software Group, Inc. End User License Agreement.
 */

import moment from 'moment';
import generalSettings from 'js-sdk/src/jrs.configs';
import { IndividualSeriesOptions } from 'highcharts';
import {
    rowsToArrayVisitor,
    getMeasures,
    getTooltipValue
} from './util/GenericSeriesUtil';
import visitMultiAxisDatasetNode from '../../../store/util/visitMultiAxisDatasetNode';
import { getEvents, getOutputParamsKeyFromField } from './util/EventUtil';
import GenericChartVisualization from './GenericChartVisualization';
import formattersFactory from '../../../formatters/formattersFactory';

export default class TimeSeriesGenericChartVisualization extends GenericChartVisualization {

    getColorsSection() :any {

        // for TimeSeries charts if user has defined series colors we want to have the whole color palette to consist
        // only from these series colors, while the default logic of the GenericChartVisualization is to extend the
        // default color palette with provided series colors.
        const formattingOptions = this.getFormattingOptions();
        const { seriesColors } = formattingOptions;
        if (Array.isArray(seriesColors) && seriesColors.length) {
            return seriesColors;
        }

        return super.getColorsSection();
    }

    // eslint-disable-next-line class-methods-use-this
    getDrilldownEnabled(): boolean {
        return false;
    }

    // eslint-disable-next-line class-methods-use-this
    getSelectedRowIndex(): number {
        return 1;
    }

    getFieldType() :string {
        const dataset = this.getDataset();
        return dataset.summary.rows.items[0].type;
    }

    getCategorizer() :string {
        const dataset = this.getDataset();
        return dataset.summary.rows.items[0].categorizer;
    }

    getFieldFormat() :string {
        const dataset = this.getDataset();
        return dataset.summary.rows.items[0].format;
    }

    getXAxisSection(selectedRowIndex: number) {
        const basicSection = super.getXAxisSection(selectedRowIndex);
        const formattingOptions = this.getFormattingOptions();
        const fieldType = this.getFieldType();
        const fieldCategorizer = this.getCategorizer();
        const fieldFormat = this.getFieldFormat();

        const {
            xAxisStep,
            xAxisRotation
        } = formattingOptions;

        const section = {
            ...basicSection,
            type: 'datetime',
            labels: {
                allowOverlap: true,
                step: xAxisStep,
                maxStaggerLines: 1,
                style: {
                    fontSize: '1em'
                },
                // eslint-disable-next-line object-shorthand, func-names
                formatter: function () :string {
                    // @ts-ignore
                    let { value } = this;

                    if (fieldCategorizer === 'day_of_week') {
                        return moment(value, 'E').format('dddd');
                    }

                    if (fieldType === 'time' || fieldType === 'timestamp') {
                        // @ts-ignore
                        value = moment(value, 'x').tz(generalSettings.userTimezone).format();
                    }

                    // @ts-ignore
                    return formattersFactory(fieldType).format(value, fieldFormat, {
                        categorizer: fieldCategorizer,
                        useUserTimezone: true
                    });
                }
            }
        };

        if (xAxisRotation !== 0) {
            // @ts-ignore
            section.labels.rotation = xAxisRotation;
        }

        // add some shift for labels if they are rotated vertically. Fixes bug #35881
        if (xAxisRotation === 90) {
            // @ts-ignore
            section.labels.x = -4;
        } else if (xAxisRotation === 270) {
            // @ts-ignore
            section.labels.x = 4;
        }

        return section;
    }

    getPlotOptionsSection(): any {
        const section = super.getPlotOptionsSection();
        return {
            ...section,
            series: {
                ...section.series,
                tooltip: {
                    ...section.series.tooltip,
                    headerFormat: '{point.point.jrs_tooltipHeader}<br/>'
                }
            }
        }
    }

    getSeriesSection(selectedColIndex: number): IndividualSeriesOptions[] {

        /**
         * This functions works just like getSeries, but takes in consideration only the row index 1
         * and produces data entries in form of { x: timestamp, y: value }
         */

        const dataset = this.getDataset();
        const linkOptions = this.getLinkOptions();
        const fieldCategorizer = this.getCategorizer();
        const staticProperties = this.getStaticProperties();
        const measures = getMeasures(staticProperties.levelsStructure);
        const measuresLevel = staticProperties.levelsStructure.columns.find((level: any) => (level.kind === 'measures'));
        const measuresLevelPosition = staticProperties.levelsStructure.columns.indexOf(measuresLevel);
        const measuresLabelToHyperlinkParam = measuresLevel.measures.reduce((memo: any, measure: any) => ({
            ...memo,
            [measure.label]: getOutputParamsKeyFromField(measure)
        }), {});
        const haveFormatInLevels = staticProperties.levelsStructure.rows.find((level: any) => {
            return Boolean(level.type && (level.aggregationFormat || level.format) && level.categorizer);
        });

        const fieldType = this.getFieldType();
        const fieldFormat = this.getFieldFormat();

        const columnSeries = this.getColumnSeries();

        const rowsArray = visitMultiAxisDatasetNode(dataset.data.rows, rowsToArrayVisitor, {
            array: [],
            indexes: [],
            stack: [],
            selectedRowIndex: this.getSelectedRowIndex()
        }, true);

        const rowsDataArray = rowsArray.array;

        const selectedRowIndex = this.getSelectedRowIndex();
        const categories = this.getCategoriesTree(selectedRowIndex, false);
        const getTooltipProps = {
            measures,
            defaultFieldAggregationType: this.defaultFieldAggregationType,
            defaultFieldAggregationFormat: this.defaultFieldAggregationFormat
        };

        let firstMinMaxRecorded :boolean = false,
            min :number = 0,
            max :number = 0;

        const series :any = [];

        columnSeries.forEach((seriesObj: any) => {
            const {
                series: serie,
                colIndex,
                measureId: measureIdFromColumn
            } = seriesObj;

            serie.data = [];
            if (linkOptions) {
                serie.cursor = 'pointer';
                serie.point = {
                    events: getEvents(linkOptions)
                };
            }

            rowsArray.indexes.forEach((rowIndex :number, itemIndex :number) => {

                const label = rowsDataArray[rowIndex].label as any;
                if (label === '' || label === null) {
                    return;
                }

                let tooltipHeader = label;
                // if categories are available then use is to form tooltip header
                if (categories && categories[rowIndex]) {
                    tooltipHeader = categories[rowIndex] as string;
                    // In AdHoc designer mode the Levels information supplied within static properties,
                    // which allows to build categories with formatted data.
                    // If Levels don't have it, then we may use default formatting settings and try to re-format labels.
                    if (!haveFormatInLevels) {
                        const convertedLabel = formattersFactory(fieldType).format(tooltipHeader, fieldFormat, {
                            categorizer: fieldCategorizer,
                            useUserTimezone: true
                        });
                        if (convertedLabel) {
                            tooltipHeader = convertedLabel;
                        }
                    }
                }

                // The value may have been passed as string.
                // With the + in front of the value, we force a conversion to number.
                const yValue = +(dataset.data.aggregations[rowIndex][colIndex]);

                if (!firstMinMaxRecorded) {
                    min = yValue;
                    max = yValue;
                    firstMinMaxRecorded = true;
                } else {
                    min = Math.min(yValue, min);
                    max = Math.max(yValue, max);
                }

                const tooltipValue = getTooltipValue(getTooltipProps, yValue, null, measureIdFromColumn);

                const linkOutputParams: any = {
                    measures: [measuresLabelToHyperlinkParam[serie.path[measuresLevelPosition]] || serie.path[measuresLevelPosition]],
                    values: [
                        {
                            key: dataset.summary.rows.items[0].hierarchicalName,
                            value: label
                        }
                    ]
                };

                let
                    xValue,
                    inMomentFormat;
                switch (fieldCategorizer) {
                case 'day':
                    inMomentFormat = `${label} 00:00:00`;
                    xValue = parseInt((moment as any).tz(inMomentFormat, generalSettings.userTimezone).format('x'), 10);
                    break;
                case 'hour':
                case 'minute':
                case 'second':
                case 'millisecond':
                    inMomentFormat = `1970-01-01 ${label}`;
                    xValue = parseInt((moment as any).tz(inMomentFormat, generalSettings.userTimezone).format('x'), 10);
                    break;
                case 'hour_by_day':
                case 'minute_by_day':
                case 'second_by_day':
                case 'millisecond_by_day':
                    // in this case 'dateLabel' contains date, time and timezone
                    inMomentFormat = `${label}`;
                    xValue = parseInt(moment(inMomentFormat).format('x'), 10);
                    break;
                case 'day_of_week':
                    xValue = parseInt(moment(label, 'dddd').format('E'), 10);
                    break;
                default:
                    inMomentFormat = `${label}`;
                    xValue = parseInt(moment(inMomentFormat).format('x'), 10);
                }

                serie.data.push({
                    // TODO: if the data is stored as timestamp, we don't need to parse the date
                    x: xValue,
                    y: yValue,
                    // this variable 'jrs_tooltipHeader' contains the text we want to show
                    // on the tooltip, and this is the way how we pass it to the function which renders the tooltip
                    jrs_tooltipHeader: tooltipHeader,
                    jrsTooltipValue: tooltipValue,
                    linkOutputParams
                });
            });

            series.push(serie);
        });

        this.recordMinMaxValues(min, max);

        return series;
    }

}
